import { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Context for managing headless mode state across the application.
 * Headless mode hides UI elements (header, debug bar, etc.) for an immersive reading experience.
 */
interface HeadlessModeContextType {
  isHeadlessMode: boolean;
  setIsHeadlessMode: (value: boolean) => void;
}

const HeadlessModeContext = createContext<HeadlessModeContextType | undefined>(undefined);

/**
 * Provider component for headless mode context.
 * Wraps the application to provide headless mode state management.
 */
export const HeadlessModeProvider = ({ children }: { children: ReactNode }) => {
  const [isHeadlessMode, setIsHeadlessMode] = useState(false);

  return (
    <HeadlessModeContext.Provider value={{ isHeadlessMode, setIsHeadlessMode }}>
      {children}
    </HeadlessModeContext.Provider>
  );
};

/**
 * Hook to access headless mode context.
 * Throws an error if used outside of HeadlessModeProvider.
 */
export const useHeadlessMode = () => {
  const context = useContext(HeadlessModeContext);
  if (context === undefined) {
    throw new Error('useHeadlessMode must be used within a HeadlessModeProvider');
  }
  return context;
};

